// BorderGlow.js — Cartão cuja BORDA acende do lado em que o cursor está.
//
// Versão enxuta do BorderGlow do react-bits. O original monta ~15 CSS variables
// a partir de uma cor HSL passada por prop, mais sete gradientes radiais de
// preenchimento e uma animação de varredura. Aqui nada disso é necessário: a
// cor é sempre o laranja da marca, que já é um token (`--c-orange`), então
// funciona nos dois temas sem parsing de HSL nenhum.
//
// Ficaram só as duas medidas que fazem o efeito existir:
//   --angulo-cursor    → direção do cursor em relação ao centro do cartão
//   --proximidade-borda → 0 no centro, 100 encostando na borda
// O gradiente cônico usa as duas: gira para o lado do cursor e acende conforme
// a pessoa se aproxima da borda. O CSS está em App.css (bloco BORDER GLOW).
//
// Diferença de propósito para o SpotlightCard, que já existe no projeto:
// o Spotlight ilumina a SUPERFÍCIE embaixo do cursor; este ilumina a BORDA.
// Dá para usar os dois juntos, mas em cima de uma capa de livro isso vira
// festa — a capa é a estrela.
import React, { useCallback, useRef } from "react";

const BorderGlow = ({
  children,
  className = "",
  as: Tag = "div",
  raio = 4,           // arredondamento da borda, em px
  sensibilidade = 30, // quanto a borda acende conforme o cursor se aproxima
  ...rest
}) => {
  const ref = useRef(null);

  const aoMover = useCallback((e) => {
    const el = ref.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left - r.width / 2;
    const y = e.clientY - r.top - r.height / 2;

    // Ângulo em graus, 0° no topo, crescendo no sentido horário.
    let angulo = Math.atan2(y, x) * (180 / Math.PI) + 90;
    if (angulo < 0) angulo += 360;

    // Proximidade da borda: projeta o cursor nos dois eixos e pega o que
    // estiver mais perto de encostar. 0 no centro, 1 na borda.
    const px = r.width ? Math.abs(x) / (r.width / 2) : 0;
    const py = r.height ? Math.abs(y) / (r.height / 2) : 0;
    const proximidade = Math.min(Math.max(px, py), 1);

    el.style.setProperty("--angulo-cursor", `${angulo.toFixed(1)}deg`);
    el.style.setProperty("--proximidade-borda", proximidade.toFixed(3));
  }, []);

  const aoSair = useCallback(() => {
    const el = ref.current;
    if (el) el.style.setProperty("--proximidade-borda", "0");
  }, []);

  return (
    <Tag
      ref={ref}
      onPointerMove={aoMover}
      onPointerLeave={aoSair}
      className={"borda-brilho " + className}
      style={{ "--raio-borda": `${raio}px`, "--sensibilidade-borda": sensibilidade }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default BorderGlow;
